import ReactECharts from "echarts-for-react";
import type { EChartsOption } from "echarts";

/**
 * Мини-тренд без осей/тултипа — «искра» из готового ряда значений.
 * Переиспользуется KPI-карточками дашборда (SPEC-013/015) и карточками
 * вёдер на /accounts (SPEC-021).
 *
 * inProgressTail — сколько последних точек считать «неполными» (пунктир):
 * для потоков (Траты/Доход/Норма) последний месяц = текущий, ещё копится.
 * Пунктир здесь = «происходит сейчас, к этому идём» (согласовано 2026-05-28).
 * На большом графике Net worth пунктир означает forecast — разные контексты,
 * не путать. Для стоков (баланс ведра, net worth) inProgressTail=0 — сплошная.
 *
 * null в values = точку посчитать нельзя (SPEC-054: месяц, где часть целевых
 * фондов без курса) — ECharts рвёт линию, и неполное значение не выдаётся за факт.
 */
export function Sparkline({
    values,
    color,
    inProgressTail = 0,
    height = 34,
}: {
    values: (number | null)[];
    color: string;
    inProgressTail?: number;
    height?: number;
}) {
    // SPEC-053: раньше возвращали null — карточки без искры схлопывались по высоте,
    // и сетка выглядела рваной, а отсутствие искры было неотличимо от бага. Первый месяц
    // жизни инстанса — штатный случай, когда точек меньше двух.
    if (values.length < 2) return <SparkPlaceholder height={height} />;
    // values без Math.round — дробные KPI (норма) иначе теряют вариацию (0.27 → 0).
    const last = values.length - 1;
    const cut = Math.max(0, last - inProgressTail);
    const fullData = values.map((v, i) => (i <= cut ? v : null));
    const tailData = values.map((v, i) => (i >= cut ? v : null));
    const option: EChartsOption = {
        backgroundColor: "transparent",
        grid: { left: 2, right: 2, top: 3, bottom: 3 },
        xAxis: { type: "category", show: false, data: values.map((_, i) => i) },
        yAxis: { type: "value", show: false, scale: true },
        tooltip: { show: false },
        series: inProgressTail > 0 ? [
            { type: "line", data: fullData, showSymbol: false, smooth: true,
              lineStyle: { width: 1.5, color }, areaStyle: { opacity: 0.1, color } },
            { type: "line", data: tailData, showSymbol: false, smooth: true,
              lineStyle: { width: 1.5, color, type: "dashed" }, areaStyle: { opacity: 0.04, color } },
        ] : [
            { type: "line", data: values, showSymbol: false, smooth: true,
              lineStyle: { width: 1.5, color }, areaStyle: { opacity: 0.1, color } },
        ],
    };
    // aria-hidden — искра декоративна: число (баланс / KPI) и так на карточке текстом.
    return (
        <div aria-hidden="true">
            <ReactECharts option={option} style={{ height, width: "100%" }} notMerge />
        </div>
    );
}

/**
 * Заглушка вместо искры: сохраняет высоту карточки и явно сообщает, что данных мало,
 * вместо того чтобы молча исчезнуть (SPEC-053). Пунктир здесь допустим — он не
 * обозначает прогноз, а показывает отсутствие ряда.
 */
function SparkPlaceholder({ height = 32 }: { height?: number }) {
    return (
        <div
            className="flex items-center justify-center text-[10px] text-muted-foreground/70"
            style={{ height }}
            aria-label="мало данных для графика"
        >
            <span className="w-full border-t border-dashed border-muted-foreground/30" />
        </div>
    );
}
